import { DisplaySettings } from '../types/Gallery';
import { DEFAULT_DISPLAY_SETTINGS } from '../config/defaults';
import { API_BASE_URL } from './apiBase';

const STORAGE_KEY = 'display_settings';
const POLL_MS = 10000;

type SettingsListener = (settings: DisplaySettings) => void;

export function mergeDisplaySettings(parsed?: Partial<DisplaySettings> | null): DisplaySettings {
  const parsedSettings = parsed || {};
  return {
    ...DEFAULT_DISPLAY_SETTINGS,
    ...parsedSettings,
    galleryCount: parsedSettings.galleryCount ?? DEFAULT_DISPLAY_SETTINGS.galleryCount,
    homepageGalleryMode: parsedSettings.homepageGalleryMode === 'all' ? 'all' : 'count',
    featuredPackageCount: parsedSettings.featuredPackageCount ?? DEFAULT_DISPLAY_SETTINGS.featuredPackageCount,
    homepageBlogCount: parsedSettings.homepageBlogCount ?? DEFAULT_DISPLAY_SETTINGS.homepageBlogCount,
    homepageGalleryStyle: parsedSettings.homepageGalleryStyle ?? DEFAULT_DISPLAY_SETTINGS.homepageGalleryStyle,
    galleryDisplayStyle: parsedSettings.galleryDisplayStyle ?? DEFAULT_DISPLAY_SETTINGS.galleryDisplayStyle,
    showGalleriesSection: parsedSettings.showGalleriesSection !== undefined ? parsedSettings.showGalleriesSection : true,
    showAboutSection: parsedSettings.showAboutSection !== undefined ? parsedSettings.showAboutSection : true,
    showServicesSection: parsedSettings.showServicesSection !== undefined ? parsedSettings.showServicesSection : true,
    showInstagramSection: parsedSettings.showInstagramSection !== undefined ? parsedSettings.showInstagramSection : true,
    showBlogSection: parsedSettings.showBlogSection !== undefined ? parsedSettings.showBlogSection : true,
    showContactSection: parsedSettings.showContactSection !== undefined ? parsedSettings.showContactSection : true,
  };
}

export function readLocalDisplaySettings(): DisplaySettings | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return mergeDisplaySettings(JSON.parse(raw));
  } catch (error) {
    console.error('Error reading local display settings:', error);
    return null;
  }
}

function rememberSettings(settings: DisplaySettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Error caching display settings:', error);
  }
}

let currentSettings = readLocalDisplaySettings() || mergeDisplaySettings(null);
const listeners = new Set<SettingsListener>();
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pendingSaveJson: string | null = null;
let saveGeneration = 0;

function cacheAndNotify(settings: DisplaySettings) {
  currentSettings = settings;
  rememberSettings(settings);
  listeners.forEach((listener) => listener(settings));
}

export function getCurrentDisplaySettings(): DisplaySettings {
  return currentSettings;
}

function stripServerMeta(settings: Partial<DisplaySettings> & { updatedAt?: string; _id?: string; _singleton?: boolean }) {
  const { updatedAt, _id, _singleton, ...rest } = settings;
  return rest;
}

async function fetchGlobalDisplaySettings(): Promise<DisplaySettings | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const res = await fetch(`${API_BASE_URL}/display-settings?v=${Date.now()}`, {
      cache: 'no-store',
      signal: controller.signal,
      headers: { 'Cache-Control': 'no-cache' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.settings || typeof data.settings !== 'object') return null;
    return mergeDisplaySettings(stripServerMeta(data.settings));
  } catch (error) {
    console.warn('Failed to load global display settings:', error);
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function refreshFromServer() {
  if (pendingSaveJson) return;
  const generation = saveGeneration;
  const remote = await fetchGlobalDisplaySettings();
  if (pendingSaveJson || generation !== saveGeneration) return;
  if (remote) cacheAndNotify(remote);
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') {
    refreshFromServer();
  }
}

function handleStorage(event: StorageEvent) {
  if (event.key !== STORAGE_KEY || !event.newValue || pendingSaveJson) return;
  try {
    cacheAndNotify(mergeDisplaySettings(JSON.parse(event.newValue)));
  } catch (error) {
    console.error('Error applying display settings from another tab:', error);
  }
}

function handleLocalUpdate(event: Event) {
  const detail = (event as CustomEvent<DisplaySettings>).detail;
  if (!detail) {
    refreshFromServer();
    return;
  }
  cacheAndNotify(mergeDisplaySettings(detail));
}

export function subscribeDisplaySettings(listener: SettingsListener) {
  listeners.add(listener);
  listener(currentSettings);

  if (listeners.size === 1) {
    refreshFromServer();
    pollTimer = setInterval(refreshFromServer, POLL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('display_settings_updated', handleLocalUpdate);
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('display_settings_updated', handleLocalUpdate);
    }
  };
}

export async function loadGlobalDisplaySettings(): Promise<DisplaySettings | null> {
  return fetchGlobalDisplaySettings();
}

export async function saveGlobalDisplaySettings(settings: DisplaySettings): Promise<boolean> {
  const merged = mergeDisplaySettings(settings);
  const generation = ++saveGeneration;
  pendingSaveJson = JSON.stringify(merged);
  cacheAndNotify(merged);
  window.dispatchEvent(new CustomEvent('display_settings_updated', { detail: merged }));

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 8000);
  try {
    const res = await fetch(`${API_BASE_URL}/display-settings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...merged, updatedAt: new Date().toISOString() }),
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`Save failed with status ${res.status}`);

    const data = await res.json().catch(() => ({}));
    if (generation !== saveGeneration) return true;

    if (data?.ignored && data.settings) {
      pendingSaveJson = null;
      cacheAndNotify(mergeDisplaySettings(stripServerMeta(data.settings)));
      return true;
    }

    pendingSaveJson = null;
    return true;
  } catch (error) {
    console.error('Error publishing display settings:', error);
    if (generation === saveGeneration) {
      pendingSaveJson = null;
      const remote = await fetchGlobalDisplaySettings();
      if (remote) cacheAndNotify(remote);
    }
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}
